import type { ModifierDefinition, ModifierId } from '../types/modifier.types';

/**
 * Single pool of intra-run modifiers. Spec: Content Bible v1 §8
 * (20 modifiers: 8 common + 8 rare + 4 epic). The code's `legendary`
 * rarity = the spec's "epic" (cf. ModifierRarity).
 *
 * Placeholder numeric values — playtest calibration.
 */
export const MODIFIER_REGISTRY: Record<ModifierId, ModifierDefinition> = {
  // ===========================================================================
  // 8 COMMON — direct boosts
  // ===========================================================================

  'mod-sharp-blade': {
    id: 'mod-sharp-blade',
    name: 'Lame-Acérée',
    type: 'small',
    rarity: 'common',
    spellEffects: [
      { handler: 'addSpellDamagePercent', params: { percent: 15 } },
    ],
    displayTemplate: '+15% dégâts au sort',
  },

  'mod-long-arm': {
    id: 'mod-long-arm',
    name: 'Long-Bras',
    type: 'small',
    rarity: 'common',
    spellEffects: [{ handler: 'addSpellRange', params: { amount: 1 } }],
    displayTemplate: '+1 portée au sort',
  },

  'mod-cadence': {
    id: 'mod-cadence',
    name: 'Cadence',
    type: 'small',
    rarity: 'common',
    spellEffects: [
      { handler: 'addSpellCooldownPercent', params: { percent: -10 } },
    ],
    displayTemplate: '-10% cooldown au sort',
  },

  'mod-jolt': {
    id: 'mod-jolt',
    name: 'Sursaut',
    type: 'small',
    rarity: 'common',
    globalEffects: [
      { handler: 'applyBuff', params: { buffId: 'mod-jolt-trigger' } },
    ],
    displayTemplate: '+20% dégâts pendant 3 s après chaque cast',
  },

  'mod-sharp-crit': {
    id: 'mod-sharp-crit',
    name: 'Crit-Aigu',
    type: 'small',
    rarity: 'common',
    globalEffects: [
      { handler: 'applyBuff', params: { buffId: 'mod-sharp-crit-buff' } },
    ],
    displayTemplate: '+10% chance critique',
  },

  'mod-lifesteal': {
    id: 'mod-lifesteal',
    name: 'Vol-de-Vie',
    type: 'small',
    rarity: 'common',
    globalEffects: [
      { handler: 'applyBuff', params: { buffId: 'mod-lifesteal-buff' } },
    ],
    displayTemplate: '5% des dégâts soignent le caster',
  },

  'mod-shockwave': {
    id: 'mod-shockwave',
    name: 'Onde-Choc',
    type: 'small',
    rarity: 'common',
    spellEffects: [
      {
        handler: 'addSpellEffectStep',
        params: {
          step: {
            handler: 'knockbackAll',
            timing: { phase: 'action', percent: 100 },
            params: { distance: 1 },
          },
        },
      },
    ],
    displayTemplate: '+1 knockback au sort',
  },

  'mod-precise': {
    id: 'mod-precise',
    name: 'Précis',
    type: 'small',
    rarity: 'common',
    spellEffects: [
      {
        handler: 'setSpellFlag',
        params: { flag: 'bypassDiagonalMiss', value: true },
      },
    ],
    displayTemplate: 'Ignore le miss diagonal-through-corner',
  },

  'mod-pierce': {
    id: 'mod-pierce',
    name: 'Perforant',
    type: 'small',
    rarity: 'common',
    spellEffects: [
      { handler: 'setSpellFlag', params: { flag: 'pierce', value: true } },
    ],
    displayTemplate: 'Le projectile du sort traverse les ennemis',
  },

  // ===========================================================================
  // 8 RARE — conditional effects
  // ===========================================================================

  'mod-cyclic-death': {
    id: 'mod-cyclic-death',
    name: 'Mort-Cyclique',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'applySpellLinkedBuff',
        params: { buffId: 'mod-cyclic-death-tracker' },
      },
    ],
    displayTemplate: '-1 s CD à chaque kill du sort',
  },

  'mod-feast': {
    id: 'mod-feast',
    name: 'Festin',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'applySpellLinkedBuff',
        params: { buffId: 'mod-feast-tracker' },
      },
    ],
    displayTemplate: '+25% dégâts pendant 5 s après kill du sort',
  },

  'mod-rich-drop': {
    id: 'mod-rich-drop',
    name: 'Drop-Riche',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'applySpellLinkedBuff',
        params: { buffId: 'mod-rich-drop-tracker' },
      },
    ],
    displayTemplate: '+50% drops sur kill du sort',
  },

  'mod-combustion': {
    id: 'mod-combustion',
    name: 'Combustion',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'applySpellLinkedBuff',
        params: { buffId: 'mod-combustion-tracker' },
      },
    ],
    displayTemplate: 'Le mob tué par ce sort explose (AOE 1, 50% dégâts)',
  },

  'mod-fragile-chain': {
    id: 'mod-fragile-chain',
    name: 'Chaîne-Fragile',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'addSpellEffectStep',
        params: {
          step: {
            handler: 'scheduleSpellRecast',
            params: { delayMs: 250, damagePercent: 50 },
          },
        },
      },
    ],
    displayTemplate: 'Le sort se relance après 250 ms (50% dégâts)',
  },

  'mod-magnet': {
    id: 'mod-magnet',
    name: 'Aimant',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'addSpellEffectStep',
        params: {
          step: {
            handler: 'pullItemsToward',
            // End of the action phase, after the spell's damage has resolved
            // (kills drop loot synchronously) so the pull catches that loot.
            timing: { phase: 'action', percent: 100 },
            params: { radius: 2 },
          },
        },
      },
    ],
    displayTemplate: 'Aspire les items au sol dans rayon 2',
  },

  'mod-long-bleed': {
    id: 'mod-long-bleed',
    name: 'Saignement-Long',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'addSpellEffectStep',
        params: {
          step: {
            handler: 'applyBuffInCells',
            params: { buffId: 'bleed-mod', targetTeam: 'opposite' },
          },
        },
      },
    ],
    displayTemplate: 'Applique saignement (DOT 5 s)',
  },

  'mod-persistent-slow': {
    id: 'mod-persistent-slow',
    name: 'Slow-Persistant',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'addSpellEffectStep',
        params: {
          step: {
            handler: 'applyBuffInCells',
            params: {
              buffId: 'persistent-slow-buff',
              targetTeam: 'opposite',
            },
          },
        },
      },
    ],
    displayTemplate: 'Ralentit 25% les cibles 4 s',
  },

  // ===========================================================================
  // 4 EPIC (`legendary` rarity in the code) — transformations
  // ===========================================================================

  'mod-triplet': {
    id: 'mod-triplet',
    name: 'Triplette',
    type: 'impactful',
    rarity: 'legendary',
    spellEffects: [
      {
        handler: 'addSpellEffectStep',
        params: {
          step: {
            handler: 'castFanSpread',
            params: { count: 2, angleDegrees: 45, damagePercent: 100 },
          },
        },
      },
    ],
    displayTemplate: 'Le sort se cast 3 fois en éventail (±45°)',
  },

  'mod-echo-strike': {
    id: 'mod-echo-strike',
    name: 'Echo-Frappe',
    type: 'impactful',
    rarity: 'legendary',
    spellEffects: [
      {
        handler: 'applySpellLinkedBuff',
        params: { buffId: 'echo-strike-tracker' },
      },
    ],
    displayTemplate: "L'attaque de base relance le sort socketé",
  },

  'mod-seeker': {
    id: 'mod-seeker',
    name: 'Traqueur',
    type: 'impactful',
    rarity: 'rare',
    globalEffects: [
      { handler: 'applyBuff', params: { buffId: 'mod-seeker-buff' } },
    ],
    displayTemplate: '+40% dégâts vs la cible la plus proche',
  },

  'mod-overclock': {
    id: 'mod-overclock',
    name: 'Overclock',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'addSpellCastTimePercent',
        params: { percent: -30 },
      },
      {
        handler: 'addSpellCooldownPercent',
        params: { percent: 40 },
      },
    ],
    displayTemplate: 'Cast -30% mais CD +40% au sort',
  },

  'mod-thorns': {
    id: 'mod-thorns',
    name: 'Epines',
    type: 'small',
    rarity: 'common',
    globalEffects: [
      { handler: 'applyBuff', params: { buffId: 'thorns-buff' } },
    ],
    displayTemplate: "Renvoie 20% des dégâts reçus à l'attaquant",
  },

  'mod-momentum': {
    id: 'mod-momentum',
    name: 'Élan',
    type: 'impactful',
    rarity: 'rare',
    spellEffects: [
      {
        handler: 'scaleDamageByCasterBuffStacks',
        params: { buffId: 'momentum-buff', perStackPercent: 5 },
      },
    ],
    globalEffects: [
      { handler: 'applyBuff', params: { buffId: 'momentum-tracker' } },
    ],
    displayTemplate: '+5% dégâts par attaque consécutive (5 stacks, 4 s)',
  },

  'mod-random-elemental': {
    id: 'mod-random-elemental',
    name: 'Élémentaire-Aléatoire',
    type: 'impactful',
    rarity: 'legendary',
    spellEffects: [
      {
        handler: 'addSpellEffectStep',
        params: {
          step: {
            handler: 'randomizeSpellElementApplyDebuff',
            params: {},
          },
        },
      },
    ],
    displayTemplate: 'Ajoute un élément aléatoire + debuff 5 s',
  },

  'mod-persistent-wave': {
    id: 'mod-persistent-wave',
    name: 'Onde-Persistante',
    type: 'impactful',
    rarity: 'legendary',
    spellEffects: [
      {
        handler: 'addSpellEffectStep',
        params: {
          step: {
            handler: 'spawnPersistentDamageZone',
            params: { durationMs: 3000, intervalMs: 500, damagePercent: 25 },
          },
        },
      },
    ],
    displayTemplate: 'Zone résiduelle qui inflige pendant 3 s',
  },
};
